import { Info } from 'lucide-react';
import { SectionIcon } from '../ui/SectionIcon.js';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { FundSummary, ResearchEstimate, ResearchStatus } from '@nemo/api';
import { dealroomIdText, dealroomInvestorUrl } from '@nemo/core/model';
import {
  columnLabel,
  currentValue,
  formatProposedValue,
  isActive,
  researchText,
} from '../research/status.js';
import { useTRPC } from '../trpc.js';
import { formatDay } from '../ui/format.js';
import { useToast } from '../ui/Toast.js';
import type { FundEdits } from './edits.js';
import { InlineText, PaneRow, Placeholder, hostOf } from './fields.js';
import { invalidateFundRows } from '../grid/invalidateFundRows.js';

export interface PaneDetailsProps {
  fund: FundSummary;
  edits: FundEdits;
  research: ResearchStatus | undefined;
  /** What a fund has recently cost, so the button says what it is about to spend. */
  estimate: ResearchEstimate | undefined;
  onStartResearch: () => void;
}

/**
 * The facts nobody opens the pane for: the names the fund also goes by, which
 * firm Dealroom thinks it is, and anything a research job is still waiting on
 * a person to decide. Folded away, because reading the pane should not mean
 * reading past them.
 */
export function PaneDetails(props: PaneDetailsProps): React.JSX.Element {
  const { fund } = props;
  const trpc = useTRPC();
  const toast = useToast();
  const queryClient = useQueryClient();

  const jobId = props.research?.jobId;
  const job = useQuery({
    ...trpc.research.get.queryOptions({ jobId: jobId ?? '' }),
    enabled: jobId !== undefined,
  });

  function refresh(): void {
    void queryClient.invalidateQueries({ queryKey: trpc.research.status.queryKey() });
    void queryClient.invalidateQueries({ queryKey: trpc.research.get.queryKey() });
    invalidateFundRows(queryClient, trpc);
    void queryClient.invalidateQueries(trpc.investors.list.pathFilter());
  }

  const onError = (error: { message: string }): void => toast(error.message);
  const decide = useMutation(
    trpc.proposals.decide.mutationOptions({ onSuccess: refresh, onError }),
  );
  const decideAll = useMutation(
    trpc.proposals.decideAll.mutationOptions({ onSuccess: refresh, onError }),
  );

  const pending = (job.data?.proposals ?? []).filter((proposal) => proposal.status === 'pending');
  const dossier = job.data?.dossier ?? null;
  const busy = decide.isPending || decideAll.isPending;
  const researchDay =
    props.research === undefined
      ? null
      : formatDay(props.research.finishedAt ?? props.research.startedAt);

  return (
    <details className="pane-section pane-details">
      <summary className="pane-section-title">
        <SectionIcon icon={Info} />
        Details
      </summary>

      <PaneRow label="Aliases">
        {fund.aliases.length === 0 ? <span className="subtle">None</span> : fund.aliases.join(', ')}
      </PaneRow>

      <PaneRow label="Website">
        <InlineText
          label={`Where ${fund.name} is on the web`}
          value={fund.website ?? ''}
          display={fund.website === null ? <Placeholder /> : hostOf(fund.website)}
          onCommit={props.edits.website}
        />
      </PaneRow>

      <PaneRow label="Dealroom">
        <DealroomLink dealroomId={fund.dealroomId} path={fund.dealroomPath} />
      </PaneRow>

      <PaneRow label="Research">
        <span className="pane-research">
          <span className="subtle">
            {researchText(props.research) === '' ? 'never run' : researchText(props.research)}
            {researchDay !== null && ` · ${researchDay}`}
            {props.estimate !== undefined && ` · ~$${props.estimate.meanCostUsd.toFixed(2)} a fund`}
          </span>
          <button
            className="button small"
            type="button"
            disabled={isActive(props.research)}
            onClick={props.onStartResearch}
          >
            Research
          </button>
        </span>
      </PaneRow>

      {props.research?.error !== null && props.research?.error !== undefined && (
        <p className="research-error">{props.research.error}</p>
      )}

      {pending.length > 0 && (
        <div className="pane-list">
          <p className="pane-proposals-head">
            <strong>
              {pending.length} {pending.length === 1 ? 'proposal' : 'proposals'}
            </strong>
            <span className="spacer" />
            <button
              className="button small"
              type="button"
              disabled={busy || jobId === undefined}
              onClick={() => decideAll.mutate({ jobId: jobId ?? '', decision: 'accept' })}
            >
              Accept all
            </button>
            <button
              className="button small"
              type="button"
              disabled={busy || jobId === undefined}
              onClick={() => decideAll.mutate({ jobId: jobId ?? '', decision: 'reject' })}
            >
              Reject all
            </button>
          </p>

          {pending.map((proposal) => (
            <div className="pane-proposal" key={proposal.id}>
              <p className="pane-proposal-line">
                <strong>{columnLabel(proposal.columnName)}</strong>
                <span className="spacer" />
                <span className="subtle">{Math.round((proposal.confidence ?? 0) * 100)}% sure</span>
              </p>
              <p className="proposal-value">
                {formatProposedValue(proposal.columnName, proposal.value)}
              </p>
              <p className="subtle">now: {currentValue(fund, proposal.columnName) || '—'}</p>
              {proposal.sources.length > 0 && (
                <p className="proposal-sources">
                  {proposal.sources.map((source) => (
                    <a key={source} href={source} target="_blank" rel="noreferrer">
                      {hostOf(source)}
                    </a>
                  ))}
                </p>
              )}
              <p className="pane-proposal-line">
                <button
                  className="button small"
                  type="button"
                  disabled={busy}
                  onClick={() => decide.mutate({ proposalId: proposal.id, decision: 'accept' })}
                >
                  Accept
                </button>
                <button
                  className="button small"
                  type="button"
                  disabled={busy}
                  onClick={() => decide.mutate({ proposalId: proposal.id, decision: 'reject' })}
                >
                  Reject
                </button>
              </p>
            </div>
          ))}
        </div>
      )}

      {dossier !== null && (
        <details className="pane-disclosure">
          <summary>Dossier</summary>
          <p className="pane-dossier">{dossier.summary}</p>
          {dossier.notable.map((item) => (
            <p className="subtle" key={item}>
              {item}
            </p>
          ))}
          <ul className="dossier-sources">
            {dossier.sources.map((source) => (
              <li key={source.url}>
                <a href={source.url} target="_blank" rel="noreferrer">
                  {source.title}
                </a>
              </li>
            ))}
          </ul>
        </details>
      )}
    </details>
  );
}

/**
 * Dealroom's pages are addressed by a slug, not by the id we hold, so a link
 * can only be offered for a fund whose slug we know. Where we do not, the id
 * is shown as text: somebody can search for it, and a link that 404s would
 * look like Dealroom had lost the firm.
 */
function DealroomLink(props: {
  dealroomId: string | null;
  path: string | null;
}): React.JSX.Element {
  const { dealroomId, path } = props;
  if (dealroomId === null) return <span className="subtle">not matched</span>;
  const url = dealroomInvestorUrl(path);
  if (url === null) {
    return (
      <span title="Dealroom's own address for this firm is not known here.">
        {dealroomIdText(dealroomId)}
      </span>
    );
  }
  return (
    <a href={url} target="_blank" rel="noreferrer">
      {path}
    </a>
  );
}
